import React, { useEffect, useState, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import { useEditor, EditorContent, ReactRenderer } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Mention from '@tiptap/extension-mention';
import { Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, Link as LinkIcon, Heading2, Code, Undo, Redo, Hash } from 'lucide-react';

const MenuButton = ({ onClick, isActive, children, title }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    className={`p-1 rounded transition-colors ${isActive ? 'bg-[#0073EA]/15 text-[#0073EA]' : 'text-[#676879] hover:bg-[#F6F7FB] dark:hover:bg-[#2a2b30] hover:text-[#323338] dark:hover:text-[#e0e0e0]'}`}
  >
    {children}
  </button>
);

/* ── Mention suggestion dropdown rendered via ReactRenderer ── */
const MentionList = forwardRef((props, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  const selectItem = (index) => {
    const item = props.items[index];
    if (item) props.command({ id: item.id, label: item.label || item.name || item.email, mentionType: item.mentionType || 'user' });
  };

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (event.key === 'ArrowUp') {
        setSelectedIndex((i) => (i + props.items.length - 1) % props.items.length);
        return true;
      }
      if (event.key === 'ArrowDown') {
        setSelectedIndex((i) => (i + 1) % props.items.length);
        return true;
      }
      if (event.key === 'Enter') {
        selectItem(selectedIndex);
        return true;
      }
      return false;
    },
  }));

  useEffect(() => setSelectedIndex(0), [props.items]);

  if (!props.items.length) return null;

  const users = props.items.filter(i => i.mentionType !== 'task');
  const tasks = props.items.filter(i => i.mentionType === 'task');

  let flatIndex = -1;
  return (
    <div
      className="bg-card border border-border rounded-lg shadow-lg z-[9999] py-1 w-72 max-h-52 overflow-y-auto"
      data-testid="rte-mention-dropdown"
    >
      {users.length > 0 && (
        <>
          <p className="px-3 py-1 text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Team Members</p>
          {users.map((item) => {
            flatIndex++;
            const idx = flatIndex;
            return (
              <button
                key={`user-${item.id}`}
                className={`flex items-center gap-2.5 w-full px-3 py-1.5 text-left text-sm transition-colors ${idx === selectedIndex ? 'bg-[#0073EA]/10' : 'hover:bg-black/[0.04] dark:hover:bg-white/[0.06]'}`}
                onClick={() => selectItem(idx)}
                onMouseEnter={() => setSelectedIndex(idx)}
                data-testid={`rte-mention-option-${idx}`}
              >
                <div className="w-6 h-6 rounded-full bg-[#0073EA] flex items-center justify-center flex-shrink-0">
                  <span className="text-[10px] text-white font-semibold">{(item.name || '?')[0].toUpperCase()}</span>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-foreground text-[13px] truncate">{item.name}</p>
                  {item.email && <p className="text-[10px] text-muted-foreground truncate">{item.email}</p>}
                </div>
              </button>
            );
          })}
        </>
      )}
      {tasks.length > 0 && (
        <>
          <p className="px-3 py-1 text-[10px] font-medium text-muted-foreground uppercase tracking-wider mt-1">Tasks</p>
          {tasks.map((item) => {
            flatIndex++;
            const idx = flatIndex;
            return (
              <button
                key={`task-${item.id}`}
                className={`flex items-center gap-2.5 w-full px-3 py-1.5 text-left text-sm transition-colors ${idx === selectedIndex ? 'bg-[#FDAB3D]/10' : 'hover:bg-black/[0.04] dark:hover:bg-white/[0.06]'}`}
                onClick={() => selectItem(idx)}
                onMouseEnter={() => setSelectedIndex(idx)}
                data-testid={`rte-mention-option-${idx}`}
              >
                <div className="w-6 h-6 rounded bg-[#FDAB3D]/15 flex items-center justify-center flex-shrink-0">
                  <Hash className="w-3.5 h-3.5 text-[#FDAB3D]" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-foreground text-[13px] truncate">{item.label}</p>
                  <p className="text-[10px] text-muted-foreground truncate">{item.sub}</p>
                </div>
              </button>
            );
          })}
        </>
      )}
    </div>
  );
});
MentionList.displayName = 'MentionList';

/* ── Build suggestion config (called once per editor mount) ── */
function buildSuggestion(teamUsersRef, tasksRef) {
  return {
    items: ({ query }) => {
      const users = (teamUsersRef.current || []).map(u => ({ ...u, mentionType: 'user' }));
      const tasks = (tasksRef.current || []).map(t => ({
        id: t.friendly_id || t.id,
        label: t.friendly_id || t.id,
        sub: t.title,
        name: t.friendly_id,
        mentionType: 'task',
      }));
      const all = [...users, ...tasks];
      if (!query) return all.slice(0, 10);
      const q = query.toLowerCase();
      return all.filter(item => {
        if (item.mentionType === 'user') {
          return item.name?.toLowerCase().includes(q) || item.email?.toLowerCase().includes(q);
        }
        return item.label?.toLowerCase().includes(q) || item.sub?.toLowerCase().includes(q);
      }).slice(0, 10);
    },
    render: () => {
      let component;
      let popup;

      return {
        onStart: (props) => {
          component = new ReactRenderer(MentionList, { props, editor: props.editor });
          popup = document.createElement('div');
          popup.style.position = 'absolute';
          popup.style.zIndex = '9999';
          popup.appendChild(component.element);
          document.body.appendChild(popup);
          const { clientRect } = props;
          if (clientRect) {
            const rect = clientRect();
            if (rect) {
              popup.style.left = `${rect.left}px`;
              popup.style.top = `${rect.bottom + 4}px`;
            }
          }
        },
        onUpdate: (props) => {
          component?.updateProps(props);
          if (popup && props.clientRect) {
            const rect = props.clientRect();
            if (rect) {
              popup.style.left = `${rect.left}px`;
              popup.style.top = `${rect.bottom + 4}px`;
            }
          }
        },
        onKeyDown: (props) => component?.ref?.onKeyDown(props),
        onExit: () => {
          component?.destroy();
          if (popup && popup.parentNode) popup.parentNode.removeChild(popup);
        },
      };
    },
  };
}

/* ── Custom Mention extension to store mentionType attribute ── */
const CustomMention = Mention.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      mentionType: {
        default: 'user',
        parseHTML: el => el.getAttribute('data-mention-type') || 'user',
        renderHTML: attrs => ({ 'data-mention-type': attrs.mentionType }),
      },
    };
  },
  renderHTML({ node, HTMLAttributes }) {
    const isTask = node.attrs.mentionType === 'task';
    const style = isTask
      ? 'color: #FDAB3D; font-weight: 600; background: rgba(253,171,61,0.12); padding: 1px 5px; border-radius: 4px; cursor: pointer;'
      : 'color: #0073EA; font-weight: 500; background: rgba(0,115,234,0.08); padding: 1px 4px; border-radius: 4px;';
    return ['span', { ...HTMLAttributes, style, class: 'mention', 'data-mention-type': node.attrs.mentionType }, `@${node.attrs.label ?? node.attrs.id}`];
  },
});

export default function RichTextEditor({ value, onChange, placeholder, className, minHeight = 100, teamUsers = [], tasks = [], onTaskMentionClick, 'data-testid': testId }) {
  const teamUsersRef = useRef(teamUsers);
  const tasksRef = useRef(tasks);
  useEffect(() => { teamUsersRef.current = teamUsers; }, [teamUsers]);
  useEffect(() => { tasksRef.current = tasks; }, [tasks]);

  const [suggestion] = useState(() => buildSuggestion(teamUsersRef, tasksRef));

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      Placeholder.configure({ placeholder: placeholder || 'Add details...' }),
      Underline,
      Link.configure({ openOnClick: false }),
      CustomMention.configure({
        HTMLAttributes: { class: 'mention' },
        suggestion,
      }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      const isEmpty = editor.isEmpty;
      onChange?.(isEmpty ? '' : html);
    },
  });

  // Handle clicks on task mentions inside the editor
  const handleEditorClick = useCallback((e) => {
    const mention = e.target.closest('[data-mention-type="task"]');
    if (mention && onTaskMentionClick) {
      const label = mention.getAttribute('data-id') || mention.textContent?.replace('@', '').trim();
      if (label) onTaskMentionClick(label);
    }
  }, [onTaskMentionClick]);

  useEffect(() => {
    if (editor && value !== undefined) {
      const current = editor.getHTML();
      const isEmpty = editor.isEmpty;
      const normalizedCurrent = isEmpty ? '' : current;
      if (normalizedCurrent !== value && !(value === '' && isEmpty)) {
        editor.commands.setContent(value || '', false);
      }
    }
  }, [value, editor]);

  if (!editor) return null;

  return (
    <div
      className={`rich-text-editor-wrapper flex flex-col ${className || ''}`}
      style={{ minHeight: minHeight + 36 }}
      data-testid={testId}
    >
      <div className="flex items-center gap-0.5 px-2 py-1 border-b border-[#E6E9EF] dark:border-[#3a3b40] bg-[#F6F7FB] dark:bg-[#1e1f23]">
        <MenuButton onClick={() => editor.chain().focus().toggleBold().run()} isActive={editor.isActive('bold')} title="Bold">
          <Bold className="w-3.5 h-3.5" />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().toggleItalic().run()} isActive={editor.isActive('italic')} title="Italic">
          <Italic className="w-3.5 h-3.5" />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().toggleUnderline().run()} isActive={editor.isActive('underline')} title="Underline">
          <UnderlineIcon className="w-3.5 h-3.5" />
        </MenuButton>
        <div className="w-px h-4 bg-[#E6E9EF] dark:bg-[#3a3b40] mx-0.5" />
        <MenuButton onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} isActive={editor.isActive('heading', { level: 2 })} title="Heading">
          <Heading2 className="w-3.5 h-3.5" />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().toggleBulletList().run()} isActive={editor.isActive('bulletList')} title="Bullet List">
          <List className="w-3.5 h-3.5" />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().toggleOrderedList().run()} isActive={editor.isActive('orderedList')} title="Numbered List">
          <ListOrdered className="w-3.5 h-3.5" />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().toggleCodeBlock().run()} isActive={editor.isActive('codeBlock')} title="Code Block">
          <Code className="w-3.5 h-3.5" />
        </MenuButton>
        <div className="w-px h-4 bg-[#E6E9EF] dark:bg-[#3a3b40] mx-0.5" />
        <MenuButton
          onClick={() => {
            const url = window.prompt('URL');
            if (url) editor.chain().focus().setLink({ href: url }).run();
          }}
          isActive={editor.isActive('link')}
          title="Link"
        >
          <LinkIcon className="w-3.5 h-3.5" />
        </MenuButton>
        <div className="flex-1" />
        <MenuButton onClick={() => editor.chain().focus().undo().run()} title="Undo">
          <Undo className="w-3.5 h-3.5" />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().redo().run()} title="Redo">
          <Redo className="w-3.5 h-3.5" />
        </MenuButton>
      </div>
      <EditorContent
        editor={editor}
        className="prose prose-sm dark:prose-invert max-w-none px-3 py-2 text-sm text-[#323338] dark:text-[#e0e0e0] flex-1 [&_.ProseMirror]:h-full [&_.ProseMirror_p.is-editor-empty:first-child::before]:text-[#9CA0AF] [&_.ProseMirror_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)] [&_.ProseMirror_p.is-editor-empty:first-child::before]:float-left [&_.ProseMirror_p.is-editor-empty:first-child::before]:pointer-events-none [&_.ProseMirror_p.is-editor-empty:first-child::before]:h-0"
        style={{ minHeight }}
        onClick={handleEditorClick}
      />
    </div>
  );
}
